import React, { useState, useMemo, useEffect, useRef } from 'react';
// import { ActivityCalendar } from 'react-activity-calendar'

import { useData } from '../hooks/useData';
import { useCore } from '../hooks/useCore';
import D3Chart from '../components/D3/D3Chart';

import ActivityCalendar from '../components/ActivityCalendar';
import { GroupedBarChart, LineChart, PieChart, ParallelSets } from '../components/D3';
import { revenueData, salesData, categoryData, monthlyTrends, pieDonutData, histogramData, treeMapData, icicleData, hexChartData, stackedBarData, scatterPlotData } from '../utils/chartData';
import { claimsFlowData } from '../utils/claimsData';
import { progressCircleData } from '../utils/chartData';
import FilterTableDrag from '../components/Table/FilterTableDrag';
import FilterTable from '../components/Table/FilterTable';
import IconButton from '../components/Input/IconButton';
import { CalendarDays } from 'lucide-react';
import { mockTableData } from '../utils/mockData';
import { useAuth } from '../hooks/useAuth';
import { Navigate, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import FileDetail from '../components/File/FileDetail';
import FileUploadButton from '../components/File/FileUploadButton';
import { ediIcons } from '../utils/codeData';
import { Mail, File, ClipboardList, Vault, ChartBar, X } from 'lucide-react';
import { useOrganization } from '../hooks/useOrganization';


const Dashboard = () => {
    const [calendarActive, setCalendarActive] = useState(true);
    const [chartActive, setChartActive] = useState(false);
    const [showFileDetail, setShowFileDetail] = useState(false);
    const { theme, navbarOpen, globalFilter } = useCore();
    const { accountSelected } = useOrganization();

    const {
        files,
        getFilesByOrg,
        uploads,
        filesQuery,
        filesOpen,
        file,
        fileOpenId,
        handleFileOpenId,
        closeOpenFile,
        claimOpenId,
        handleClaimOpenId
    } = useData();

    const { user } = useAuth();
    const hasRefreshedRef = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [activeTab, setActiveTab] = useState('vault');

    // Configurable pre-leader time in months
    const PRE_LEADER_MONTHS = 3;
    

    // Check if we're on a file detail route
    useEffect(() => {
        const pathParts = location.pathname.split('/');
        if (pathParts.length > 2 && pathParts[1] === 'file') {
            setShowFileDetail(true);
            handleFileOpenId(pathParts[2]);
            if(pathParts[3] !== undefined){
                handleClaimOpenId(pathParts[3]);
            }else{
                handleClaimOpenId(null);
            }
        } else {
            setShowFileDetail(false);
            handleFileOpenId(null);
            handleClaimOpenId(null);
        }
    }, [location.pathname]);

    const handleFileDetailClose = () => {
        setShowFileDetail(false);
        navigate('/dashboard');
    };

    useEffect(() => {
        if(user && accountSelected) {
                        getFilesByOrg();
        }
    }, [ user, accountSelected ]);

    // Refresh files when uploads complete - optimized to prevent constant firing
    useEffect(() => {
        // Only check if we have uploads and haven't already refreshed
        if (uploads.length > 0 && !hasRefreshedRef.current) {
            const allUploadsComplete = uploads.every(file => file.progress >= 100);
            const hasIncompleteUploads = uploads.some(file => file.progress < 100 && file.status !== 'error');
            
            // Only refresh when all uploads are complete and we haven't refreshed yet
            if (allUploadsComplete && !hasIncompleteUploads) {
                hasRefreshedRef.current = true;
                                // Wait a moment for the server to process, then refresh files
                setTimeout(() => {
                    getFilesByOrg();
                }, 1000);
            }
        }
        
        // Reset the ref when uploads array becomes empty (new upload session)
        if (uploads.length === 0) {
            hasRefreshedRef.current = false;
        }
    }, [uploads, getFilesByOrg]);

    // Create calendar data from real files data
    const calendarData = useMemo(() => {
                const activityMap = new Map();
        
        // Count uploads by date from real files data
        if (files && files.length > 0) {
            files.forEach(file => {
                const date = file.uploaded ? file.uploaded.split('T')[0] : new Date().toISOString().split('T')[0];
                const count = activityMap.get(date) || 0;
                activityMap.set(date, count + 1);
                            });
        } else {
                    }
        
        // Convert to the format expected by ActivityCalendar
        const data = [];
        activityMap.forEach((count, date) => {
            // Determine level based on count (1-4 levels)
            let level;
            if (count <= 2) level = 1;
            else if (count <= 4 ) level = 2;
            else if (count <= 6 ) level = 3;
            else level = 4; // 4+ uploads
            
            data.push({
                date,
                count,
                level
            });
        });
        
                        
        // Sort by date
        data.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // --- Calculate the date range with constraints ---
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Start of today
        
        let startDate, endDate;
        
        if (data.length > 0) {
            // Find the earliest file date
            const earliestFileDate = new Date(data[0].date);
            
            // Calculate start date: PRE_LEADER_MONTHS months before the earliest file date
            startDate = new Date(earliestFileDate);
            startDate.setMonth(startDate.getMonth() - PRE_LEADER_MONTHS);
            startDate.setDate(1); // Start of the month
            
            // End date is today (not beyond)
            endDate = new Date(today);
        } else {
            // If no files, show PRE_LEADER_MONTHS months before today
            startDate = new Date(today);
            startDate.setMonth(startDate.getMonth() - PRE_LEADER_MONTHS);
            startDate.setDate(1); // Start of the month
            
            endDate = new Date(today);
        }
        
        // Generate all dates in the range
        const allDates = [];
        const currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
            const dateStr = currentDate.toISOString().slice(0, 10);
            const existingData = data.find(item => item.date === dateStr);
            
            if (existingData) {
                allDates.push(existingData);
            } else {
                allDates.push({
                    date: dateStr,
                    count: 0,
                    level: 0
                });
            }
            
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
                
        return allDates;
    }, [files]);

    const handleCalendarClick = () => {
        setCalendarActive(!calendarActive);
            };

    const handleChartClick = () => {
        setChartActive(!chartActive);
            };

    const vaultTabClick = () => {
        setActiveTab('vault');
        // setShowFileDetail(false);
        handleFileOpenId(null);
        navigate('/dashboard');
    }

    const onClaimTabClick = (slug) => {
        handleFileOpenId(slug);
        navigate(`/file/${slug}`);
    }

    const onCloseTabClick = (slug, event) => {
        event.stopPropagation();
        
        if(fileOpenId === slug){
            handleFileOpenId(null);
            navigate('/dashboard');
        }
            

        
        closeOpenFile(slug);
        
    }

    return accountSelected ? (
        <div className={`main-content page-dashboard ${calendarActive ? 'calendar-active' : ''}`}>
           
            
            <div className="page-header">
                <div className="page-header-left">
                    {/* <FileUploadButton /> */}
                </div>
                { claimOpenId ? (
                    <p style={{margin:0}}>Claim Open ID: {claimOpenId}</p>
                ):null}
                <div className="page-header-right">
                    <IconButton 
                        icon={<ChartBar />}
                        onChange={handleChartClick}
                        active={chartActive}
                        size={16}
                        style={{
                            // position: 'absolute',
                            // top: '11px',
                            // right: '44px',
                            // zIndex: 10
                        }}
                    />
                    <IconButton 
                        icon={<CalendarDays />}
                        onChange={handleCalendarClick}
                        active={calendarActive}
                        size={16}
                        style={{
                            // position: 'absolute',
                            // top: '11px',
                            // right: '11px',
                            // zIndex: 10
                        }}
                    />
                    
                </div>
                
            </div>


            {calendarActive ? (
                <ActivityCalendar data={calendarData} precedingMonths={PRE_LEADER_MONTHS} />
            ) : (
                <></>
            )}

            

            {/* D3 Charts Section */}

            {chartActive ? (
                <div className="chart-grid">
                    <D3Chart
                        type="grouped-bar"
                        size="medium"
                        data={revenueData}
                        legend={false}
                        aspect="horizontal"
                    axisX={true}
                    axisY={true}
                    title="Revenue by Category"
                   
                />

                <D3Chart
                    type="histogram"
                    size="large"
                        data={histogramData}
                        axisX={true}
                        axisY={true}
                        legend={false}
                        labels={false}
                        title="Histogram"
                    />
                
                <D3Chart
                    type="pie-donut"
                    size="small"
                        data={pieDonutData}
                        axisX={false}
                        axisY={false}
                        legend={false}
                        labels={true}
                        title="Pie and donuts"
                    />

                    <D3Chart 
                    type="progress-circle"
                    data={progressCircleData}
                    maxValue={100}
                    size="small"
                    startAngle={Math.PI / 2}
                    colorScheme={['#4299e1', '#48bb78', '#ed8936']}
                    />
                
                    <D3Chart
                        type="parallel-sets"
                        size="full"
                        data={claimsFlowData}
                        axisX={true}
                        axisY={true}
                        legend={false}
                        title="Claims Flow Analysis"
                    />
                
                    <D3Chart
                        type="tree-map"
                        panelClass="teal"
                        size="full"
                        data={treeMapData}
                        axisX={true}
                        axisY={true}
                        legend={false}
                        title="Tree Map"
                    />
                
                    <D3Chart
                        type="icicle"
                        panelClass="coral"
                        size="full"
                        data={icicleData}
                        axisX={true}
                        axisY={true}
                        legend={false}
                        title="Icicle Chart"
                    />
                
                    <D3Chart
                        type="hex-chart"
                        panelClass="lavend"
                        size="xlarge"
                        axisX={true}
                        axisY={true}
                        legend={false}
                        data={hexChartData}
                        radius={30}
                        title="Hex Chart"
                    />

                    <D3Chart
                        type="stacked-bar"
                        size="large"
                        data={stackedBarData}
                        axisX={true}
                        axisY={true}
                        legend={true}
                        title="Stacked Bar Chart"
                        margin={{ top: 40, right: 30, bottom: 20, left: 50 }}
                    />

                    <D3Chart
                        type="scatter-plot"
                        size="large"
                        data={scatterPlotData}
                        axisX={true}
                        axisY={true}
                        legend={false}
                        title="Scatter Plot"
                        xLabel="Height (inches)"
                        yLabel="Weight (lbs)"
                        radius={4}
                        labels={true}
                        margin={{ top: 40, right: 30, bottom: 50, left: 60 }}
                    />
                
                
                
                
                </div>
            ) : null }
            
            
       
            
            <div className="file-tabs">
                
                <div onClick={() => vaultTabClick()} className={`file-tab${activeTab === 'vault' && !fileOpenId ? ' active' : ''}`}>
                    <File color="var(--brand-blue)" style={{marginRight: '5px'}} size={16} /> vault
                </div>

                {/* { showFileDetail && file && fileOpenId === file.slug ? (
                    <div onClick={() => setActiveTab('vault')} className={`file-tab${showFileDetail ? ' active' : ''}`}>
                        <ClipboardList color="var(--brand-blue)" style={{marginRight: '5px'}} size={16} /> {file.name}
                    </div>
                ) : null } */}

                { Object.keys(filesOpen).length > 0 ? (
                    <>
                        {Object.values(filesOpen).map((file) => (
                            <div key={file.slug} onClick={() => onClaimTabClick(file.slug)} className={`file-tab${fileOpenId === file.slug ? ' active' : ''}`}>
                                <ClipboardList color="var(--brand-blue)" style={{marginRight: '5px',minWidth: '16px'}} size={16} />
                                <p>{file.name}</p>
                                <div onClick={(e) => onCloseTabClick(file.slug, e)} className={`close-button-circle small ${theme}`}>
                                    <X size={14} />
                                </div>
                            </div>
                        ))}
                    </>
                ) : null }
                
            </div>

            {activeTab === 'vault' && !fileOpenId && (
                <>
                    <FilterTable data={filesQuery.length > 0 && globalFilter !== '' ? filesQuery : files} />
                </>
            )}
           
            { fileOpenId ? (
                <FileDetail 
                    coreFile={fileOpenId ? filesOpen[fileOpenId] : null} 
                    onClose={handleFileDetailClose}
                    calendarActive={calendarActive}
                />
            ):null}
    
                
            
       </div>
    ) : (
        <div className="main-content page-dashboard">
            <p style={{textAlign: 'center', marginTop: 50, fontSize: '18px', fontWeight: 'bold'}}>Please select an account to view the claims.</p>
        </div>
    );
};

export default Dashboard;
